import { customToolbarEpd } from 'components/form/customToolbar';
import { ContentState, EditorState, convertFromHTML, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { useEffect, useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import styled from 'styled-components';

import useIsReadOnlyMode from '../compiler/hooks/useIsReadOnlyMode';
import SmartLabel from '../labels/SmartLabel';
import { ExpandableHelpHtml } from '../help-boxes/ExpandableHelpHtml';
import { normalizeRichTextEditorValue } from './richTextEditorValue';

const WizardHTMLEditor = ({
  name,
  label,
  tooltip,
  tooltipPosition,
  labelStyle,
  value,
  error,
  onChanged,
  disabled,
  containerClassName,
  helpText,
  ...props
}: any) => {
  const sanitizeHtml = (html: string) => {
    return html.replace(/<ins>/g, '<u>').replace(/<\/ins>/g, '</u>');
  };

  const createEditorState = (value: string) => {
    const cleanHtml = sanitizeHtml(value || '');
    const blocksFromHTML = convertFromHTML(cleanHtml);
    const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
    return EditorState.createWithContent(contentState);
  };

  const [editorState, setEditorState] = useState<EditorState | null>(createEditorState(value));

  // Keep editor content in sync when the source value changes externally
  // (e.g. refetch after navigating back to dashboard and reopening document).
  useEffect(() => {
    setEditorState(createEditorState(value));
  }, [value]);

  const contentState = editorState!.getCurrentContent();
  const newValue = normalizeRichTextEditorValue(contentState.getPlainText(), draftToHtml(convertToRaw(contentState)));
  const isReadOnly = useIsReadOnlyMode() || disabled;

  return (
    <div className={containerClassName}>
      {label && (
        <SmartLabel
          label={label}
          required={props.required}
          labelStyle={labelStyle}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={newValue.length > 0}
          error={error}
        />
      )}
      {helpText && !isReadOnly && (
        <div style={{ marginBottom: 10 }}>
          <ExpandableHelpHtml html={helpText} />
        </div>
      )}
      <StyledEditorWrapper isReadOnly={isReadOnly}>
        <Editor
          toolbarHidden={false}
          toolbar={customToolbarEpd}
          editorState={editorState}
          onEditorStateChange={(newEditorState) => setEditorState(newEditorState)}
          onBlur={() => {
            onChanged(name, newValue);
          }}
          readOnly={isReadOnly}
          {...props}
        />
      </StyledEditorWrapper>
    </div>
  );
};

const StyledEditorWrapper = styled.div<{ isReadOnly: boolean }>`
  .rdw-option-wrapper {
    background-color: ${(props) =>
      props.isReadOnly ? props.theme.colors.elementsInputBorder : props.theme.colors.secondaryLightGreen30};
    pointer-events: ${(props) => (props.isReadOnly ? 'none' : 'all')};
  }
  .rdw-editor-main {
    border: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
    padding: 0 1rem;
    font-size: 14px;
    height: fit-content;
    min-height: 100px;
    max-height: 400px;
    border-radius: 2px;
    &:focus-within,
    &:hover {
      border-color: ${(props) =>
        props.isReadOnly ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange};
    }
    background-color: ${(props) =>
      props.isReadOnly ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryWhite};
  }
`;

export default WizardHTMLEditor;
